import { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { getEvents } from "@/lib/api";
import { ChevronLeft, ChevronRight, MapPin, Clock, ExternalLink } from "lucide-react";

const MONTHS = [
    "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
    "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];
const WEEKDAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

function ymd(year, month, day) {
    const m = String(month + 1).padStart(2, "0");
    const d = String(day).padStart(2, "0");
    return `${year}-${m}-${d}`;
}

export default function EventsCalendarWidget() {
    const [cursor, setCursor] = useState(() => {
        const now = new Date();
        return { year: now.getFullYear(), month: now.getMonth() };
    });
    const [events, setEvents] = useState([]);
    const [activeEvents, setActiveEvents] = useState(null);
    const [activeDate, setActiveDate] = useState(null);

    useEffect(() => {
        const ym = `${cursor.year}-${String(cursor.month + 1).padStart(2, "0")}`;
        getEvents(ym).then(setEvents).catch(() => setEvents([]));
    }, [cursor]);

    const eventsByDate = useMemo(() => {
        const map = {};
        events.forEach((e) => {
            if (!map[e.date]) map[e.date] = [];
            map[e.date].push(e);
        });
        return map;
    }, [events]);

    const firstDay = new Date(cursor.year, cursor.month, 1).getDay();
    const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();

    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);

    const prev = () => {
        setCursor((c) => {
            const m = c.month - 1;
            if (m < 0) return { year: c.year - 1, month: 11 };
            return { year: c.year, month: m };
        });
    };
    const next = () => {
        setCursor((c) => {
            const m = c.month + 1;
            if (m > 11) return { year: c.year + 1, month: 0 };
            return { year: c.year, month: m };
        });
    };

    const onDayClick = (day) => {
        if (!day) return;
        const key = ymd(cursor.year, cursor.month, day);
        const dayEvents = eventsByDate[key];
        if (dayEvents && dayEvents.length) {
            setActiveEvents(dayEvents);
            setActiveDate(key);
        }
    };

    // Themed palette swatches for visual interest, like the template
    const swatches = [
        { bg: "bg-[#F5C53D]", text: "text-[#1B1B1B]" },
        { bg: "bg-[#E63B26]", text: "text-white" },
        { bg: "bg-[#54547A]", text: "text-white" },
        { bg: "bg-[#A89489]", text: "text-white" },
        { bg: "bg-[#B8C0A8]", text: "text-[#1B1B1B]" },
    ];

    return (
        <aside data-testid="events-calendar-widget">
            <h3 className="section-title text-3xl mb-4">Events Calendar</h3>
            <div className="bg-[#E63B26] text-white p-4 overflow-hidden">
                <div className="flex items-center justify-between mb-3">
                    <button
                        onClick={prev}
                        aria-label="Previous month"
                        className="p-1 hover:opacity-70 transition"
                        data-testid="calendar-prev"
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                    <div className="flex items-baseline gap-2 font-poppins">
                        <span className="bg-[#F5C53D] text-[#1B1B1B] px-3 py-1 text-lg font-semibold tracking-wider">
                            {cursor.year}
                        </span>
                        <span className="text-2xl font-bold tracking-[0.18em]">
                            {MONTHS[cursor.month]}
                        </span>
                    </div>
                    <button
                        onClick={next}
                        aria-label="Next month"
                        className="p-1 hover:opacity-70 transition"
                        data-testid="calendar-next"
                    >
                        <ChevronRight className="h-5 w-5" />
                    </button>
                </div>

                <div className="grid grid-cols-7 gap-1 text-center font-poppins">
                    {WEEKDAYS.map((w) => (
                        <div
                            key={w}
                            className="text-[#F5C53D] text-xs tracking-[0.1em] py-1"
                        >
                            {w}
                        </div>
                    ))}
                    {cells.map((day, idx) => {
                        const cellKey = `${cursor.year}-${cursor.month}-${idx}`;
                        if (!day) return <div key={cellKey} className="aspect-square" />;
                        const key = ymd(cursor.year, cursor.month, day);
                        const hasEvent = !!eventsByDate[key];
                        const swatch = swatches[day % swatches.length];
                        return (
                            <button
                                key={cellKey}
                                onClick={() => onDayClick(day)}
                                disabled={!hasEvent}
                                className={`aspect-square flex items-center justify-center text-base font-medium relative transition ${
                                    hasEvent
                                        ? `${swatch.bg} ${swatch.text} hover:scale-105 cursor-pointer ${
                                              hasEvent ? "calendar-day-event" : ""
                                          }`
                                        : "bg-white/15 text-white/90"
                                }`}
                                data-testid={`calendar-day-${day}${
                                    hasEvent ? "-event" : ""
                                }`}
                                aria-label={`Day ${day}${
                                    hasEvent ? ", has events" : ""
                                }`}
                            >
                                {day}
                            </button>
                        );
                    })}
                </div>
            </div>

            <Dialog
                open={!!activeEvents}
                onOpenChange={(o) => {
                    if (!o) setActiveEvents(null);
                }}
            >
                <DialogContent
                    className="bg-white text-[#1B1B1B] sm:max-w-lg max-h-[80vh] overflow-y-auto"
                    data-testid="event-detail-modal"
                >
                    <DialogHeader>
                        <DialogTitle className="font-poppins text-2xl text-[#E63B26]">
                            {activeDate
                                ? new Date(activeDate).toLocaleDateString(undefined, {
                                      weekday: "long",
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                  })
                                : ""}
                        </DialogTitle>
                        <DialogDescription className="font-sofia">
                            {activeEvents?.length || 0} event(s) on this day
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-5 mt-2">
                        {(activeEvents || []).map((e) => (
                            <article
                                key={e.id}
                                className="border-l-4 border-[#F5C53D] pl-4 py-1"
                                data-testid={`event-item-${e.id}`}
                            >
                                <h4 className="font-poppins font-semibold text-lg">
                                    {e.title}
                                </h4>
                                <div className="mt-1 flex flex-wrap items-center gap-3 text-sm font-sofia text-[#1B1B1B]/80">
                                    {e.time && (
                                        <span className="inline-flex items-center gap-1">
                                            <Clock className="h-3.5 w-3.5" />
                                            {e.time}
                                        </span>
                                    )}
                                    {e.location && (
                                        <span className="inline-flex items-center gap-1">
                                            <MapPin className="h-3.5 w-3.5" />
                                            {e.location}
                                        </span>
                                    )}
                                </div>
                                {e.description && (
                                    <p className="mt-2 font-sofia text-[#1B1B1B]/80 leading-relaxed">
                                        {e.description}
                                    </p>
                                )}
                                {e.link && (
                                    <a
                                        href={e.link}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="mt-2 inline-flex items-center gap-1 text-[#E63B26] font-poppins text-sm tracking-[0.1em] uppercase hover:underline"
                                        data-testid={`event-link-${e.id}`}
                                    >
                                        Details <ExternalLink className="h-3.5 w-3.5" />
                                    </a>
                                )}
                            </article>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </aside>
    );
}
